import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientListClasificacion, ClientListEstado, ClientListItem } from '../types/client';
import { api } from '../api';
import Topbar from './ui/Topbar';
import Input from './ui/Input';
import SingleSelect from './ui/SingleSelect';
import Select from './ui/Select';
import Badge from './ui/Badge';

type Props = {
  embedded?: boolean;
};

const ClientList: React.FC<Props> = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [codeSearch, setCodeSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [status, setStatus] = useState<ClientListEstado | ''>('Activo');
  const [classification, setClassification] = useState<ClientListClasificacion | ''>('');
  const [herramienta, setHerramienta] = useState<'' | 'APP' | 'B2B' | 'B2C'>('');
  const [cloud, setCloud] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<
    | ''
    | 'codigocliente'
    | 'nombre'
    | 'razonsocial'
    | 'estado'
    | 'clasificacion'
    | 'herramienta'
    | 'licencias_activas'
    | 'fechaalta'
    | 'fechabaja'
    | 'servidor'
  >('');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');

  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSort = (next: NonNullable<typeof sortBy>) => {
    if (next === '') {
      setSortBy('');
      setSortDir('ASC');
      return;
    }
    if (sortBy !== next) {
      setSortBy(next);
      setSortDir('ASC');
      return;
    }
    setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
  };

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    if (codeSearch.trim()) params.set('codigo', codeSearch.trim());
    if (nameSearch.trim()) params.set('nombre', nameSearch.trim());
    if (status) params.set('estado', status);
    if (classification) params.set('clasificacion', classification);
    if (herramienta) params.set('herramienta', herramienta);
    if (cloud) params.set('cloud', cloud);
    if (sortBy) {
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);
    }
    return `?${params.toString()}`;
  }, [classification, cloud, codeSearch, herramienta, nameSearch, page, pageSize, sortBy, sortDir, status]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getClients(query);
        if (!cancelled) {
          setClients(res.data);
          setTotal(res.pagination.total);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Error al cargar clientes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const pageButtons = useMemo(() => {
    const current = safePage;
    const last = totalPages;
    if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);

    const items: Array<number | '...'> = [];
    const push = (v: number | '...') => {
      if (items.length === 0 || items[items.length - 1] !== v) items.push(v);
    };

    push(1);
    const left = Math.max(2, current - 1);
    const right = Math.min(last - 1, current + 1);
    if (left > 2) push('...');
    for (let p = left; p <= right; p++) push(p);
    if (right < last - 1) push('...');
    push(last);
    return items;
  }, [safePage, totalPages]);

  React.useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  return (
    <div
      id={embedded ? undefined : 'list-screen'}
      className={embedded ? 'has-sticky-footer' : 'screen active has-sticky-footer'}
      data-list-query={query}
    >
      {embedded ? null : (
        <Topbar
          title="Clientes"
          right={
            <span className="text-sm text-secondary">
              {loading ? 'Cargando…' : `${total} clientes`}
            </span>
          }
        />
      )}

      {error ? (
        <div className="p-4 text-sm text-red-600">{error}</div>
      ) : null}

      <div className="table-wrap">
        <table className="clients-table">
          <colgroup>
            <col style={{ width: '12.195%' }} />
            <col style={{ width: '26.016%' }} />
            <col style={{ width: '9.756%' }} />
            <col style={{ width: '9.756%' }} />
            <col style={{ width: '9.756%' }} />
            <col style={{ width: '8.130%' }} />
            <col style={{ width: '8.130%' }} />
            {status === 'Baja' && <col style={{ width: '8.130%' }} />}
            <col style={{ width: '8.130%' }} />
          </colgroup>
          <thead>
            <tr>
              {(
                [
                  { col: 'codigocliente', label: 'Código', style: { maxWidth: 200 } },
                  { col: 'nombre', label: 'Nombre' },
                  { col: 'estado', label: 'Estado' },
                  { col: 'clasificacion', label: 'Clasif.' },
                  { col: 'herramienta', label: 'Herramienta' },
                  { col: 'licencias_activas', label: 'Licencias' },
                  { col: 'fechaalta', label: 'Alta', style: { maxWidth: 300 } },
                  ...(status === 'Baja' ? [{ col: 'fechabaja' as const, label: 'Baja' }] : []),
                  { col: 'servidor', label: 'Infra' },
                ] as Array<{ col: typeof sortBy; label: string; style?: React.CSSProperties }>
              ).map(({ col, label, style }) => (
                <th
                  key={col}
                  className={sortBy === col ? 'colname sort-active' : 'colname'}
                  style={style}
                  onClick={() => toggleSort(col)}
                >
                  {label}
                  <i className="sort-icon">
                    {sortBy === col ? (sortDir === 'ASC' ? '▲' : '▼') : '⇅'}
                  </i>
                </th>
              ))}
            </tr>
            <tr className="table-filters">
              <th className="colfilter">
                <div className="table-search">
                  <Input
                    value={codeSearch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setCodeSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Buscar"
                    className="table-input ui-input-search"
                  />
                  {codeSearch.trim().length > 0 ? (
                    <button
                      type="button"
                      className="table-clear"
                      aria-label="Borrar búsqueda"
                      onClick={() => { setCodeSearch(''); setPage(1); }}
                    >
                      <span className="icon-s icon-discard" />
                    </button>
                  ) : null}
                </div>
              </th>
              <th className="colfilter">
                <div className="table-search">
                  <Input
                    value={nameSearch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setNameSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Buscar"
                    className="table-input ui-input-search"
                  />
                  {nameSearch.trim().length > 0 ? (
                    <button
                      type="button"
                      className="table-clear"
                      aria-label="Borrar búsqueda"
                      onClick={() => { setNameSearch(''); setPage(1); }}
                    >
                      <span className="icon-s icon-discard" />
                    </button>
                  ) : null}
                </div>
              </th>
              <th className="colfilter">
                <SingleSelect
                  value={status}
                  onChange={(next) => { setStatus(next as ClientListEstado | ''); setPage(1); }}
                  options={[
                    { value: '', label: 'Todos' },
                    { value: 'Activo', label: 'Activo' },
                    { value: 'Baja', label: 'Baja' },
                    { value: 'Impago', label: 'Impago' },
                    { value: 'Obsoleto', label: 'Obsoleto' },
                    { value: 'Potencial', label: 'Potencial' },
                  ]}
                />
              </th>
              <th className="colfilter">
                <SingleSelect
                  value={classification}
                  onChange={(next) => { setClassification(next as ClientListClasificacion | ''); setPage(1); }}
                  options={[
                    { value: '', label: 'Todos' },
                    { value: 'vip', label: 'VIP' },
                    { value: 'oro', label: 'Oro' },
                    { value: 'plata', label: 'Plata' },
                    { value: 'bronce', label: 'Bronce' },
                    { value: 'otros', label: 'Otros' },
                  ]}
                />
              </th>
              <th className="colfilter">
                <SingleSelect
                  value={herramienta}
                  onChange={(next) => { setHerramienta(next as '' | 'APP' | 'B2B' | 'B2C'); setPage(1); }}
                  options={[
                    { value: '', label: 'Todas' },
                    { value: 'B2B', label: 'B2B' },
                    { value: 'APP', label: 'APP' },
                    { value: 'B2C', label: 'B2C' },
                  ]}
                />
              </th>
              <th className="colfilter" />
              <th className="colfilter" />
              {status === 'Baja' && <th className="colfilter" />}
              <th className="colfilter">
                <SingleSelect
                  value={cloud}
                  onChange={(next) => { setCloud(next as '' | 'true' | 'false'); setPage(1); }}
                  options={[
                    { value: '', label: 'Todos' },
                    { value: 'true', label: 'Cloud' },
                    { value: 'false', label: 'On-Premise' },
                  ]}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c: ClientListItem) => (
              <tr key={c.codigocliente} onClick={() => navigate(`/cliente/${c.codigocliente}`)}>
                <td>{c.codigocliente}</td>
                <td>{c.nombre}</td>
                <td>
                  <Badge variant={c.estado}>{c.estado}</Badge>
                </td>
                <td>
                  {c.clasificacion && c.clasificacion !== 'otros' ? <Badge variant={c.clasificacion}>{c.clasificacion.toUpperCase()}</Badge> : '—'}
                </td>
                <td>{c.herramienta || '—'}</td>
                <td>{c.licencias_activas ?? '—'}</td>
                <td>{c.fechaalta?.slice(0, 10) ?? '—'}</td>
                {status === 'Baja' && <td>{c.fechabaja?.slice(0, 10) ?? '—'}</td>}
                <td className="text-xs text-secondary">{c.servidor ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-left">
          <span>Registros: {total}</span>
          <Select
            className="pagination-size"
            value={String(pageSize)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </Select>
        </div>

        <div className="pagination-center">
          <button
            type="button"
            className="pager-arrow"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <span className="icon-s icon-menorque" />
          </button>
          {pageButtons.map((p, idx) => {
            if (p === '...') {
              return <span key={`dots-${idx}`} className="pager-dots">…</span>;
            }
            return (
              <button
                key={p}
                type="button"
                className={p === safePage ? 'pager-page active' : 'pager-page'}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            );
          })}
          <button
            type="button"
            className="pager-arrow next"
            disabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <span className="icon-s icon-mayorque" />
          </button>
        </div>

        <div className="pagination-right" />
      </div>
    </div>
  );
};

export default ClientList;
